/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '..';
import vopayApi from '../../services/api.config';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  vp.ELinxSingleUseTokenResponse,
  (requestParams: vp.ELinxSingleUseTokenRequest) => void,
  () => void,
  (requestParams: vp.ELinxSingleUseTokenRequest) => Promise<vp.ELinxSingleUseTokenResponse>,
];

function useFetchPaylinkSingleUseToken({ api }: HookProps): ReturnTuple {
  const { t } = useTranslation('translations', { keyPrefix: 'Common' });
  const source = axios.CancelToken.source();

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [params, setParams] = useState<vp.ELinxSingleUseTokenRequest>();
  const [response, setResponse] = useState<vp.ELinxSingleUseTokenResponse>({
    JWT: '',
    ErrorMessage: '',
    Success: false,
    Token: '',
    IsValidToken: false,
    ExtraInfo: {
      PayLinkDetails: {
        FirstName: '',
        LastName: '',
        ReceiverName: '',
        SenderEmailAddress: '',
        ReceiverEmailAddress: '',
        InvoiceNumber: '',
        Note: '',
        SenderName: '',
        Amount: '',
        Currency: '',
        Status: '',
        FailureReason: '',
        Link: '',
        IframeURL: '',
        IframeKey: '',
        TransactionID: '',
        ScheduledTransactionID: '',
        EFTType: '',
        PaymentType: '',
        TransactionDate: '',
        Frequency: '',
        NameOfFrequency: '',
        SemiMonthlyFrequencyType: '',
        ScheduleEndDate: '',
        EndingAfterPayments: '',
        Type: '',
        CanDecline: '',
        ScheduleStartDate: '',
        LastModified: '',
        ExpireDate: 30,
        BankAccountLinked: false,
        BankAccountToken: '',
        RecipientMessage: '',
        FlexiblePaymentOptions: '',
        MinimumPaymentAccepted: '',
        RemainderPaymentDate: '',
        ReceiverPhoneNumber: '',
        PaymentMethodDisplayOptions: 'New',
        ClientAccountID: '',
        DisplayEmbbedInterac: false,
        BankAccountRail: ['eft'],
        DocumentLink: '',
      },
      VerificationDetails: {
        VerificationRequestID: '',
        Amount: '',
        SenderEmailAddress: '',
        ReceiverEmailAddress: '',
        Link: '',
        VerificationStatus: '',
        VerificationAttempts: '',
        VerificationStartDate: '',
        VerificationAttemptDate: '',
        ExpireDate: '',
      },
    },
    Name: '',
    LegalBusinessName: '',
    BusinessAddress: {
      Address1: '',
      Address2: '',
      City: '',
      Province: '',
      Country: '',
    },
    ContactAddress: {
      Address1: '',
      Address2: '',
      City: '',
      Province: '',
      PostalCode: '',
      Country: '',
    },
    IsPartnerToken: false,
    AccountStatus: '',
    PartnerAccountType: '',
    SubAccountType: '',
  });
  const [shouldSendRequest, setSendRequest] = useState(false);

  useEffect(() => {
    const sendRequest = async (requestParams: vp.ELinxSingleUseTokenRequest) => {
      setDataFetchingInfo(true, `${t('defaultLoading')}`, 'fullScreen');
      const res = await api.fetchPaylinkSingleUseToken(requestParams, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.ELinxSingleUseTokenRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setSendRequest(false);
      setParams(undefined);
      setResponse({
        JWT: '',
        ErrorMessage: '',
        Success: false,
        Token: '',
        IsValidToken: false,
        ExtraInfo: {
          PayLinkDetails: {
            FirstName: '',
            LastName: '',
            ReceiverName: '',
            SenderEmailAddress: '',
            ReceiverEmailAddress: '',
            InvoiceNumber: '',
            Note: '',
            SenderName: '',
            Amount: '',
            Currency: '',
            Status: '',
            FailureReason: '',
            Link: '',
            IframeURL: '',
            IframeKey: '',
            TransactionID: '',
            ScheduledTransactionID: '',
            EFTType: '',
            PaymentType: '',
            TransactionDate: '',
            Frequency: '',
            NameOfFrequency: '',
            SemiMonthlyFrequencyType: '',
            ScheduleEndDate: '',
            EndingAfterPayments: '',
            Type: '',
            CanDecline: '',
            ScheduleStartDate: '',
            LastModified: '',
            ExpireDate: 30,
            BankAccountLinked: false,
            BankAccountToken: '',
            RecipientMessage: '',
            FlexiblePaymentOptions: '',
            MinimumPaymentAccepted: '',
            RemainderPaymentDate: '',
            ReceiverPhoneNumber: '',
            PaymentMethodDisplayOptions: 'New',
            ClientAccountID: '',
            DisplayEmbbedInterac: false,
            BankAccountRail: ['eft'],
            DocumentLink: '',
          },
          VerificationDetails: {
            VerificationRequestID: '',
            Amount: '',
            SenderEmailAddress: '',
            ReceiverEmailAddress: '',
            Link: '',
            VerificationStatus: '',
            VerificationAttempts: '',
            VerificationStartDate: '',
            VerificationAttemptDate: '',
            ExpireDate: '',
          },
        },
        Name: '',
        LegalBusinessName: '',
        BusinessAddress: {
          Address1: '',
          Address2: '',
          City: '',
          Province: '',
          Country: '',
        },
        ContactAddress: {
          Address1: '',
          Address2: '',
          City: '',
          Province: '',
          PostalCode: '',
          Country: '',
        },
        IsPartnerToken: false,
        AccountStatus: '',
        PartnerAccountType: '',
        SubAccountType: '',
      });
    },
    async (requestParams: vp.ELinxSingleUseTokenRequest): Promise<vp.ELinxSingleUseTokenResponse> => {
      const asyncSource = axios.CancelToken.source();
      const res = await api.fetchPaylinkSingleUseToken(requestParams, asyncSource);
      setResponse(res);
      return res;
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchPaylinkSingleUseToken);
